export class NumberService {
  /**
   * Форматирует число в формате локали.
   * @param amount - Число или строка, приводимая к числу
   * @returns Отформатированная строка, например "1 234,56"
   */
  public static toLocaleNumber(amount: number | string, locale: string = "ru"): string {
    return new Intl.NumberFormat(locale).format(Number(amount));
  }

  /**
   * Форматирует число в кратком виде: до 999 — как есть, дальше — с "к", "кк", "ккк" и т.д.
   * Дробная часть через запятую, один знак при необходимости (1,5к; 999,9кк).
   * @param amount - Число или строка, приводимая к числу
   * @returns Например: "500", "1к", "1,5к", "999,9к", "1кк", "999,9кк", "1ккк"
   */
  public static toLocaleShortNumber(amount: number | string, locale: string = "ru"): string {
    const num = Number(amount);

    if (num < 1000) {
      return new Intl.NumberFormat("ru").format(num);
    }

    let divisor = 1000;
    let suffix = "к";

    while (num >= divisor * 1000) {
      divisor *= 1000;
      suffix += "к";
    }

    const value = num / divisor;
    const rounded = Math.floor(value * 10) / 10;
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(rounded);

    return formatted + suffix;
  }

  /**
   * Форматирует число в формате локали или кратком (к/кк) и добавляет символ валюты.
   * @param amount - Число или строка, приводимая к числу
   * @param currency - Код валюты (ISO 4217), по умолчанию "RUB"
   * @param locale - Локаль, по умолчанию "ru"
   * @param short - Если true — краткий формат через toLocaleShortNumber, иначе toLocaleNumber
   * @returns Например "3 500,00 ₽" или "1,5к ₽"
   */
  public static toLocaleCurrency(
    amount: number | string,
    short: boolean = false,
    locale: string = "ru",
    currency: string = "RUB",
  ): string {
    const formatted = short
      ? NumberService.toLocaleShortNumber(amount, locale)
      : NumberService.toLocaleNumber(amount, locale);

    const symbol =
      new Intl.NumberFormat(locale, { style: "currency", currency }).formatToParts(0).find((p) => p.type === "currency")
        ?.value ?? currency;

    return `${formatted} ${symbol}`;
  }
}
